import { useCallback, useEffect, useRef, useState } from 'react'

export type MonitorConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

interface UseCallMonitorOptions {
  listenUrl: string | undefined
  enabled?: boolean
}

interface UseCallMonitorReturn {
  isListening: boolean
  toggleListening: () => void
  connectionState: MonitorConnectionState
  audioLevel: number
  error: string | null
}

/**
 * Hook that connects to VAPI's listenUrl WebSocket and plays back
 * raw PCM audio via the Web Audio API.
 *
 * VAPI's monitor stream is 16kHz, 16-bit signed LE PCM, **stereo interleaved**:
 * channel 0 = customer, channel 1 = assistant. We down-mix to mono for playback.
 * (Treating it as mono produces garbled audio — effectively 8kHz with samples
 * alternating between two unrelated speakers, which sounds like static.)
 */
export function useCallMonitor({ listenUrl, enabled = false }: UseCallMonitorOptions): UseCallMonitorReturn {
  const [isListening, setIsListening] = useState(enabled)
  const [connectionState, setConnectionState] = useState<MonitorConnectionState>('disconnected')
  const [audioLevel, setAudioLevel] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const levelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastLevelRef = useRef(0)
  const nextStartTimeRef = useRef(0)

  const cleanup = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current)
      connectTimeoutRef.current = null
    }
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current)
      levelIntervalRef.current = null
    }
    if (wsRef.current) {
      // Detach handlers before close so a late onclose can't flip state back
      // on a disposed session (cleanup is also called on toggle-off/unmount).
      wsRef.current.onopen = null
      wsRef.current.onmessage = null
      wsRef.current.onerror = null
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    nextStartTimeRef.current = 0
    setConnectionState('disconnected')
    setAudioLevel(0)
  }, [])

  const connect = useCallback(() => {
    if (!listenUrl || !isListening) return

    cleanup()
    setError(null)
    setConnectionState('connecting')

    if (import.meta.env.DEV) {
      console.info('[useCallMonitor] opening ws', listenUrl)
    }
    const ws = new WebSocket(listenUrl)
    wsRef.current = ws

    const audioCtx = new AudioContext({ sampleRate: 48000 })
    audioCtxRef.current = audioCtx
    audioCtx.resume().catch(() => {})

    const SAMPLE_RATE = 16000
    const BYTES_PER_SAMPLE = 2

    ws.binaryType = 'arraybuffer'

    // Safety net: if the handshake doesn't complete in 10s, give up and let
    // the user retry instead of being stuck in "Connecting" forever.
    connectTimeoutRef.current = setTimeout(() => {
      if (wsRef.current === ws && ws.readyState !== WebSocket.OPEN) {
        setError('Listen stream did not respond — try again.')
        setIsListening(false)
      }
    }, 10_000)

    ws.onopen = () => {
      if (import.meta.env.DEV) console.info('[useCallMonitor] ws open')
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current)
        connectTimeoutRef.current = null
      }
      setConnectionState('connected')
      setError(null)

      // Audio level indicator: poll ~10Hz
      levelIntervalRef.current = setInterval(() => {
        setAudioLevel(lastLevelRef.current)
        lastLevelRef.current *= 0.85 // decay
      }, 100)
    }

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        // JSON metadata frame — ignore
        return
      }

      const arrayBuffer = event.data as ArrayBuffer
      // Need at least one stereo frame (4 bytes)
      if (arrayBuffer.byteLength < BYTES_PER_SAMPLE * 2) return

      const int16 = new Int16Array(arrayBuffer)
      // Stereo interleaved: ch0=customer, ch1=assistant. Drop a trailing
      // orphan sample if the buffer length is odd (shouldn't happen, but guard).
      const numFrames = int16.length >> 1

      // Down-mix to mono in one pass and compute RMS on the mixed signal.
      const mono = new Float32Array(numFrames)
      let sumSq = 0
      for (let i = 0; i < numFrames; i++) {
        const customer = int16[2 * i] / 32768
        const assistant = int16[2 * i + 1] / 32768
        const m = (customer + assistant) * 0.5
        mono[i] = m
        sumSq += m * m
      }
      lastLevelRef.current = Math.sqrt(sumSq / numFrames)

      const ratio = audioCtx.sampleRate / SAMPLE_RATE
      const outputLength = Math.ceil(numFrames * ratio)
      const audioBuffer = audioCtx.createBuffer(1, outputLength, audioCtx.sampleRate)
      const output = audioBuffer.getChannelData(0)

      for (let i = 0; i < outputLength; i++) {
        const srcIdx = i / ratio
        const idx = Math.floor(srcIdx)
        const frac = srcIdx - idx
        const s0 = idx < numFrames ? mono[idx] : 0
        const s1 = idx + 1 < numFrames ? mono[idx + 1] : s0
        output[i] = s0 + frac * (s1 - s0) // linear interpolation
      }

      const source = audioCtx.createBufferSource()
      source.buffer = audioBuffer
      source.connect(audioCtx.destination)

      // If scheduler has drifted far ahead of real time (backgrounded tab,
      // burst of frames), snap back so latency doesn't keep growing.
      const now = audioCtx.currentTime
      if (nextStartTimeRef.current - now > 0.3) {
        nextStartTimeRef.current = now
      }
      const startTime = Math.max(nextStartTimeRef.current, now)
      source.start(startTime)
      nextStartTimeRef.current = startTime + audioBuffer.duration
    }

    ws.onerror = (ev) => {
      if (import.meta.env.DEV) console.warn('[useCallMonitor] ws error', ev)
      setError('Could not connect to the listen stream.')
      setConnectionState('error')
    }

    ws.onclose = (event) => {
      if (import.meta.env.DEV) {
        console.info('[useCallMonitor] ws closed', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        })
      }
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current)
        connectTimeoutRef.current = null
      }
      // Codes 1000 (normal), 1005 (no status), and 1006 (no close frame) are
      // all things VAPI emits when the call ends or the stream is torn down —
      // not user-facing errors. Any other code signals an abnormal close.
      const isCleanClose = event.code === 1000 || event.code === 1005 || event.code === 1006
      if (!isCleanClose) {
        setError(`Listen stream closed (code ${event.code}).`)
      }
      // Always flip listening off so the button returns to idle instead of
      // being stuck at "Connecting" / lying that we're still connected.
      setIsListening(false)
      setConnectionState('disconnected')
    }
  }, [listenUrl, isListening, cleanup])

  // Connect/disconnect when listening state or URL changes
  useEffect(() => {
    if (isListening && listenUrl) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      connect()
    } else {
      cleanup()
    }
    return cleanup
  }, [isListening, listenUrl, connect, cleanup])

  const toggleListening = useCallback(() => {
    setIsListening((prev) => !prev)
  }, [])

  return { isListening, toggleListening, connectionState, audioLevel, error }
}
